/**
 * Chronological history for a request: workflow actions and comments together.
 * Status changes show an explicit From → To trace with who acted and when.
 */
import React from 'react';
import {
  ArrowRight,
  CheckCircle,
  ExternalLink,
  FilePlus,
  Info,
  MessageCircle,
  Paperclip,
  Rocket,
  XCircle,
} from 'lucide-react';
import { formatCommentHtml } from '../../../utils/formatCommentHtml';

const COMMENT_KINDS = new Set([
  'comment',
  'stage_transition',
  'info_request',
  'info_response',
  'routed_to_it',
  'escalated_to_dot',
  'system',
]);

const TRACE_KINDS = new Set([
  'created',
  'status_change',
  'info_request',
  'info_response',
  'routed_to_it',
  'escalated_to_dot',
  'system',
  'attachment',
]);

const getInitial = (entry) =>
  (entry.actorName || entry.actorEmail || 'U').charAt(0).toUpperCase();

const getIcon = (entry) => {
  if (entry.kind === 'created') {
    return <FilePlus size={15} style={{ color: '#6366f1' }} />;
  }
  if (entry.kind === 'attachment') {
    return <Paperclip size={15} style={{ color: '#6366f1' }} />;
  }
  if (entry.kind === 'info_request' || entry.toStage === 'needs_info') {
    return <Info size={15} style={{ color: '#3b82f6' }} />;
  }
  if (entry.kind === 'routed_to_it' || entry.kind === 'escalated_to_dot') {
    return <ExternalLink size={15} style={{ color: '#c2410c' }} />;
  }
  if (entry.kind === 'system' || entry.toStage === 'published') {
    return <Rocket size={15} style={{ color: '#16a34a' }} />;
  }
  if (entry.toStage === 'rejected') {
    return <XCircle size={15} style={{ color: '#dc2626' }} />;
  }
  if (entry.kind === 'status_change') {
    return <CheckCircle size={15} style={{ color: '#f97316' }} />;
  }
  return <MessageCircle size={15} style={{ color: '#94a3b8' }} />;
};

const TransitionPath = ({ entry }) => {
  const fromLabel = entry.fromStageLabel || entry.fromStage;
  const toLabel = entry.toStageLabel || entry.toStage;
  if (!toLabel && !fromLabel) return null;
  return (
    <div className="activity-trace-path" aria-label="Status transition">
      {fromLabel ? <span className="activity-trace-stage">{fromLabel}</span> : null}
      {fromLabel && toLabel ? (
        <ArrowRight size={12} className="activity-trace-arrow" aria-hidden />
      ) : null}
      {toLabel ? (
        <span className={`activity-trace-stage is-to${entry.isCurrent ? ' is-current' : ''}`}>
          {toLabel}
        </span>
      ) : null}
    </div>
  );
};

const ActorLine = ({ entry }) => {
  const who = entry.actorName || entry.actorEmail;
  if (!who && !entry.team) return null;
  return (
    <div className="activity-trace-actor">
      {who ? <span>By {who}</span> : null}
      {who && entry.team ? <span className="activity-trace-sep">·</span> : null}
      {entry.team ? <span>{entry.team}</span> : null}
    </div>
  );
};

const RequestActivityFeed = ({ entries, TimestampDisplay, userEmail }) => {
  if (!entries || entries.length === 0) {
    return (
      <div className="comment-empty-state">
        <MessageCircle size={32} style={{ opacity: 0.3 }} />
        <p>No activity yet.</p>
      </div>
    );
  }

  return (
    <div className="activity-feed">
      {entries.map((entry) => {
        const hasBody = Boolean(String(entry.text || '').trim());
        // Any entry with a real body is a comment card (incl. stage_transition)
        const isComment = hasBody && (COMMENT_KINDS.has(entry.kind) || entry.kind === 'stage_transition');
        const isTrace = TRACE_KINDS.has(entry.kind) || Boolean(entry.toStage);
        const classes = [
          entry.kind,
          entry.isCurrent ? 'is-current' : '',
          isTrace ? 'is-trace' : '',
        ]
          .join(' ')
          .trim();

        if (!isComment) {
          return (
            <div key={entry.id} className={`activity-event ${classes}`}>
              <span className="activity-event-icon">{getIcon(entry)}</span>
              <div className="activity-event-main">
                <TransitionPath entry={entry} />
                <div
                  className="activity-event-text"
                  dangerouslySetInnerHTML={{ __html: formatCommentHtml(entry.title) }}
                />
                <ActorLine entry={entry} />
                {entry.detail && <div className="activity-event-detail">{entry.detail}</div>}
                {hasBody ? (
                  <div
                    className="comment-body"
                    style={{ marginTop: '0.35rem' }}
                    dangerouslySetInnerHTML={{ __html: formatCommentHtml(entry.text) }}
                  />
                ) : null}
              </div>
              <span className="activity-event-time">
                <TimestampDisplay timestamp={entry.createdAt} userEmail={userEmail} />
              </span>
            </div>
          );
        }

        return (
          <div key={entry.id} className={`activity-comment comment-item ${classes}`}>
            <div className="comment-header">
              <div className="comment-author">
                <div className="comment-avatar">{getInitial(entry)}</div>
                <div className="comment-author-info">
                  <TransitionPath entry={entry} />
                  <div
                    className="activity-comment-title"
                    dangerouslySetInnerHTML={{ __html: formatCommentHtml(entry.title) }}
                  />
                  <div className="comment-meta">
                    <ActorLine entry={entry} />
                    {entry.detail && (
                      <span className="activity-event-detail">{entry.detail}</span>
                    )}
                    <span className="comment-time">
                      <TimestampDisplay timestamp={entry.createdAt} userEmail={userEmail} />
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="comment-body"
              dangerouslySetInnerHTML={{ __html: formatCommentHtml(entry.text) }}
            />
          </div>
        );
      })}
    </div>
  );
};

export default RequestActivityFeed;
